import React from 'react';
import { Card } from '../ui';
import { 
  ChartBarIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  PlayIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import processService from '../../services/processService';

const ProcessStatistics = ({ statistics, timeframe }) => {
  if (!statistics) {
    return (
      <Card>
        <div className="p-8 text-center">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Statistics Available</h3>
          <p className="text-gray-500">Statistics will appear here once processes are available.</p>
        </div>
      </Card>
    );
  }

  const { totals, by_type, by_status } = statistics;

  const getTimeframeLabel = () => {
    switch (timeframe) {
      case '1h': return 'Last Hour';
      case '24h': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      default: return 'Last 24 Hours';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed': return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'in_progress': return <PlayIcon className="h-5 w-5 text-blue-500" />;
      case 'pending': return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'cancelled': return <StopIcon className="h-5 w-5 text-gray-500" />;
      default: return <ChartBarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Total Processes</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {totals.total_processes.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PlayIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Active</div>
                <div className="text-2xl font-semibold text-blue-600">
                  {totals.active_processes.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Completed</div>
                <div className="text-2xl font-semibold text-green-600">
                  {totals.completed_processes.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Failed</div>
                <div className="text-2xl font-semibold text-red-600">
                  {totals.failed_processes.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Item Processing Stats */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Item Processing Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold text-gray-900">
                {totals.total_items.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Total Items</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-blue-600">
                {totals.processed_items.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Processed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-green-600">
                {totals.successful_items.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Successful</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-600">
                {totals.failed_items.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Failed</div>
            </div>
          </div>
          
          {/* Success Rate */}
          {totals.processed_items > 0 && (
            <div className="mt-4 text-center">
              <div className="text-sm text-gray-500">Overall Success Rate</div>
              <div className="text-xl font-semibold text-gray-900">
                {Math.round((totals.successful_items / totals.processed_items) * 100)}%
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* By Process Type */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">By Process Type</h3>
          <div className="space-y-4">
            {Object.entries(by_type).map(([type, stats]) => (
              <div key={type} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{processService.getProcessTypeIcon(type)}</span>
                  <div>
                    <div className="font-medium text-gray-900">
                      {processService.getProcessTypeDisplayName(type)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {stats.total} processes
                    </div>
                  </div>
                </div>
                <div className="flex space-x-4 text-sm">
                  <div className="text-center">
                    <div className="font-medium text-blue-600">{stats.in_progress || 0}</div>
                    <div className="text-gray-500">Active</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-green-600">{stats.completed || 0}</div>
                    <div className="text-gray-500">Done</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-red-600">{stats.failed || 0}</div>
                    <div className="text-gray-500">Failed</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* By Status */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">By Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(by_status).map(([status, count]) => {
              const statusInfo = processService.getStatusDisplayInfo(status);
              return (
                <div key={status} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(status)}
                    <div>
                      <div className="font-medium text-gray-900">{statusInfo.text}</div>
                      <div className="text-sm text-gray-500">{status}</div>
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Time Frame Info */}
      <Card>
        <div className="p-4 bg-blue-50 border border-blue-200">
          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 text-blue-500" />
            <div className="ml-3">
              <div className="text-sm font-medium text-blue-900">Time Frame</div>
              <div className="text-sm text-blue-700">
                Statistics shown for: {getTimeframeLabel()}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProcessStatistics;
